import { ReactComponent as DeleteIcon } from "assets/icons/delete.svg";
import { MeasurementType } from "modules/common/components/Alarms/constants";
import { Button } from "modules/common/components/Button/Button";
import { FC, useState, useEffect, useMemo } from "react";
import Select from "react-select";
import { useGetCultures, useGetPhenophases } from "modules/phytoview/hooks/phytoViewHook";
import { Culture, PhytoviewPhenophase } from "types/phytoview";
import { IconButton } from "modules/common/components/Alarms/Form/components/IconButton";

interface PhenophaseInputProps {
    keyValue: MeasurementType;
    title: string;
    phenophaseId?: number;
    onCultureChange: (keyValue: MeasurementType, value?: number) => void;
    onPhenophaseChange: (keyValue: MeasurementType, value?: number | null) => void;
    removePhenophaseInput: (keyValue: MeasurementType) => void;
}

const PhenophaseInput: FC<PhenophaseInputProps> = ({
    keyValue,
    title,
    phenophaseId,
    onCultureChange,
    onPhenophaseChange,
    removePhenophaseInput,
}) => {
    const [selectedCulture, setSelectedCulture] = useState<Culture | null>(null);
    const [selectedPhenophase, setSelectedPhenophase] = useState<PhytoviewPhenophase | null>(null);

    const culturesData = useGetCultures() as unknown as Culture[];
    const phenophases = useGetPhenophases() as unknown as PhytoviewPhenophase[];

    const filteredPhenophases = useMemo(() => {
        if (!selectedCulture) {
            return [];
        }

        return phenophases.filter(p => p.predmetOpazanjaId?.id === selectedCulture.id);
    }, [phenophases, selectedCulture]);

    useEffect(() => {
        if (phenophaseId === null || phenophaseId === undefined) {
            return;
        }

        const phenophase = phenophases.find(p => p.id === phenophaseId);

        if (!phenophase) {
            return;
        }

        const culture = culturesData.find(c => c.id === phenophase.predmetOpazanjaId?.id);

        if (!culture) {
            return;
        }

        setSelectedCulture(culture);
        setSelectedPhenophase(phenophase);
    }, [phenophaseId, phenophases, culturesData]);

    const handleCultureChange = (selectedItem: Culture | null) => {
        try {
            const cultureValue = selectedItem?.id;
            setSelectedCulture(selectedItem);
            onCultureChange(keyValue, cultureValue);

            setSelectedPhenophase(null);
            onPhenophaseChange(keyValue, null);
        } catch (error) {
            console.error("Error in handleCultureChange:", error);
        }
    };

    const handlePhenophaseChange = (selectedItem: PhytoviewPhenophase | null) => {
        try {
            const phenophaseValue = selectedItem?.id;
            setSelectedPhenophase(selectedItem);
            onPhenophaseChange(keyValue, phenophaseValue);
        } catch (error) {
            console.error("Error in handlePhenophaseChange:", error);
        }
    };

    return (
        <div className="alarms-range-inputs">
            <label className="alarms-form-label">{title}</label>
            <div className="alarms-range-treshold-icon-and-inputs">
                <div className="alarms-form-range-treshold-container">
                    <div>
                        <label>Kultura</label>
                        <Select
                            placeholder="Odaberite kulturu"
                            options={culturesData}
                            value={selectedCulture}
                            getOptionLabel={(c: Culture) => c.name}
                            getOptionValue={(c: Culture) => String(c.id)}
                            onChange={handleCultureChange}
                        />
                    </div>
                    <div>
                        <label>Fenofaza</label>
                        <Select
                            placeholder="Odaberite fenofazu"
                            options={filteredPhenophases}
                            value={selectedPhenophase}
                            getOptionLabel={(p: PhytoviewPhenophase) => p.name}
                            getOptionValue={(p: PhytoviewPhenophase) => String(p.id)}
                            onChange={handlePhenophaseChange}
                            isDisabled={!selectedCulture}
                        />
                    </div>
                    <IconButton onClick={() => removePhenophaseInput(keyValue)} type="button">
                        <DeleteIcon />
                    </IconButton>
                </div>
            </div>
        </div>
    );
};

export default PhenophaseInput;
